import * as React from 'react'
import { Logger } from '@utils'
import { PlayerCapabilities, ContentConfig } from 'ATVWebPlayer'
import { ClientsideLogger } from '../../util/logger/logger'
import { createPlayer, PlayerProxy, HistoryItem, PlayerEvent } from './integration'
import * as playerConfig from './integration/config'
import './player.module.scss'

export { playerConfig }

const ERROR_PLAYER_NOT_READY = 'DVWebPlayer:notReady'
const ERROR_PLAYBACK_ABORTED = 'DVWebPlayer:aborted'
const ERROR_PLAYBACK_MISSING_DRM = 'DVWebPlayer:missingDrm'

export type PlayerContentConfig = ContentConfig & {
  lcid?: string
  ref?: string
}

export type PlaybackContentLink = {
  contentConfig: PlayerContentConfig
  signInUrl?: string
  playbackUrl: string
  fallbackUrl?: string
}

export type PlayerIntegrationProps = {
  os?: string
  logger?: Logger
  playerConfig: playerConfig.PlayerConfigData
  playbackLinkData: PlaybackContentLink | undefined
  whisperCacheData: PlayerContentConfig | undefined
  onAsinChanged?: (config: HistoryItem) => unknown
  onPlay?: (config: PlayerContentConfig) => unknown
  onPlayFailure?: () => void
  onClosed?: (config: HistoryItem) => unknown
  onInitialized?: () => void
  onPrepare?: () => void
  onPlayerCapabilities?: (capabilities: PlayerCapabilities) => void
  liveIncompatibleCallback: () => void
}

type Overflow = 'hidden' | 'visible'

const setBodyOverflow = (overflow: Overflow) => {
  document.getElementsByTagName('body')[0].style.overflow = overflow
}

const setPlayerClass = (className: string) => {
  document.getElementById(playerConfig.PLAYER_ELEMENT_ID)!.className = className
}

const showVideo = (_content: PlayerContentConfig) => {
  setPlayerClass('dv-player-fullscreen')
  setBodyOverflow('hidden')
}

const hideVideo = (_content: PlayerContentConfig) => {
  setPlayerClass('')
  setBodyOverflow('visible')
}

export const navigate = (url: string) => {
  window.location.assign(url)
}

export class PlayerIntegration extends React.Component<PlayerIntegrationProps> {
  private readonly logger: Logger
  private player?: PlayerProxy
  private unsubscribeHandlers: (() => unknown)[]

  constructor(props: PlayerIntegrationProps) {
    super(props)
    this.logger =
      props.logger ||
      new ClientsideLogger(
        `DVWebNode:Player:${this.props.playerConfig.pageType}`
      )
    this.unsubscribeHandlers = []
  }

  logFatal(error: string) {
    this.count(error).fatal(error)
  }

  count(counter: string) {
    const { pageType } = this.props.playerConfig
    return this.logger.count(`${counter}:${pageType}`, 1, true)
  }

  async attachRefreshEventHandlers() {
    const {
      onPlay,
      onClosed,
      onAsinChanged,
      onInitialized,
      onPrepare,
      onPlayerCapabilities
    } = this.props

    const player = this.player!
    const addEvent = (event: PlayerEvent, handler: (content: PlayerContentConfig) => void) => {
      this.unsubscribeHandlers.push(player.on(event, (c) => handler(c)))
    }

    addEvent('play', showVideo)
    onPlay && addEvent('play', onPlay)
    addEvent('closed', hideVideo)
    onClosed && addEvent('closed', onClosed)
    onAsinChanged && addEvent('asinChanged', onAsinChanged)
    onInitialized && addEvent('initialized', onInitialized)
    onPrepare && addEvent('prepare', onPrepare)
    addEvent('aborted', () => {
      this.count(ERROR_PLAYBACK_ABORTED)
    })

    const capabilities = await player.capabilities
    if (!capabilities.supportsDrm) {
      this.count(ERROR_PLAYBACK_MISSING_DRM)
    }
    onPlayerCapabilities && onPlayerCapabilities(capabilities)
  }

  async componentDidMount() {
    const {
      playerConfig,
      whisperCacheData,
      liveIncompatibleCallback
    } = this.props
    try {
      this.player = await createPlayer(playerConfig, liveIncompatibleCallback)
      this.attachRefreshEventHandlers()
      if (whisperCacheData) {
        await this.handlePrepareRequest(whisperCacheData)
      }
    } catch (error) {
      this.logFatal(error)
    }
  }

  componentDidUpdate({
    playbackLinkData: previousLinkData,
    whisperCacheData: previousWhisperCacheData
  }: PlayerIntegrationProps) {
    const { playbackLinkData, whisperCacheData } = this.props
    if (playbackLinkData && !previousLinkData) {
      this.handlePlayRequest(playbackLinkData)
    }

    if (
      !playbackLinkData &&
      whisperCacheData &&
      whisperCacheData !== previousWhisperCacheData
    ) {
      this.handlePrepareRequest(whisperCacheData)
    }
  }

  async componentWillUnmount() {
    this.unsubscribeHandlers.forEach(cb => cb())
  }

  async handlePrepareRequest(contentConfig: PlayerContentConfig) {
    if (this.player) {
      this.player.prepare(contentConfig)
    }
  }

  async handlePlayRequest({
    contentConfig,
    signInUrl
  }: PlaybackContentLink) {
    const { onPlayFailure } = this.props
    let playSuccess = true

    if (!this.player) {
      this.count(ERROR_PLAYER_NOT_READY)
      playSuccess = false
    } else if (signInUrl) {
      navigate(signInUrl)
    } else {
      try {
        await this.player.play(contentConfig)
      } catch (e) {
        playSuccess = false
      }
    }

    if (!playSuccess && onPlayFailure) {
      onPlayFailure()
    }
  }

  render(): null {
    return null
  }
}

